import {createRedux} from '../../../redux/factories/common/reduxFactory';
import {equals} from 'ramda';

const {actionTypes, actions} = createRedux([
    'addAlternativeStep',
    'addField',
    'addStep',
    'addAlternativeStep',
    'changeFieldName',
    'changeImage',
    'clearTemplate',
    'deleteStep',
    'detectBoundaries',
    'finishUserGuide',
    'loadTemplate',
    'resetRedirectList',
    'saveTemplate',
    'selectField',
    'setControlMode',
    'setEnableMouseMove',
    'setForceUpdateActiveTab',
    'setGeneralInformation',
    'toggleAlternative',
    'switchField',
    'toggleUserGuide',
    'updateBoundaries',
    'uploadImage',
    'useImage',
]);


const INIT_STATE = {
    // Template Creator
    redirectToList: false,
    isEditTemplate: false,
    templateSession: null,
    uploadImage: null,
    template: null,
    activeTab: 1,
    forceUpdateActiveTab: false,
    showUserGuide: !!!localStorage.getItem('skipUserGuide'),
    currentPage: 0,

    // Template Attributes
    templateName: '',
    description: '',
    removeAccent: false,
    fullText: false,
    detectQR: false,
    detectSignature: false,
    detectMRZ: false,
    skipImageProcessing: false,
    scannedDocument: false,
    pages: {
        0: {
            page: 0,
            fields: [],
        }
    },

    // Fields
    selectedField: null,
    locationMap: {},

    // Annotation
    controlMode: null,
    enableBack: true,
    enableTextLocation: true,
    enableValidator: false,
    enableFormat: false,
    enableMouseMove: false,
    enableSaveAction: false,
    focusAnnotationTool: true,
    selectedTextLocation: undefined,
    showAlternative: false,
    selectedStep: null,
    selectedNode: null,

    // Image Editor
    documentBoundaries: [],
    imageRatio: 1.0,

    loading: false,
};

// REDUCER

const templateReducers = (state = INIT_STATE, action) => {
    if (action.type === actionTypes['deleteStepSuccess']) {
        const {selectedField} = action.payload;
        return {...state, selectedField: selectedField};
    } else if (action.type === actionTypes['setForceUpdateActiveTab']) {
        return {...state, forceUpdateActiveTab: action.payload};
    } else if (action.type === actionTypes['selectField']) {
        return {...state, selectedField: action.payload};
    } else if (action.type === actionTypes['detectBoundaries']) {
        return {...state, loading: true};
    } else if (action.type === actionTypes['detectBoundariesSuccess']) {
        const {corners, session} = action.payload.data.data;
        return {...state, documentBoundaries: corners, templateSession: session, loading: false};
    } else if (action.type === actionTypes['detectBoundariesFailed']) {
        return {...state, loading: false};
    } else if (action.type === actionTypes['updateBoundaries']) {
        return {...state, documentBoundaries: action.payload};
    } else if (action.type === actionTypes['uploadImageSuccess']) {
        return {...state, uploadImage: action.payload};
    } else if (action.type === actionTypes['changeImage']) {
        return INIT_STATE;
    } else if (action.type === actionTypes['useImage']) {
        return {...state, loading: true};
    } else if (action.type === actionTypes['useImageSuccess']) {
        let {image, textBoxes, ratio, w, h} = action.payload.data;
        const page = {page: 0, w, h, image, boxes: textBoxes, fields: [], templateImage: image};
        return {...state, imageRatio: ratio, loading: false, pages: {'0': page}};
    } else if (action.type === actionTypes['addField']) {
        const {controlMode} = action.payload;
        return {
            ...state,
            controlMode,
        };
    } else if (action.type === actionTypes['addFieldSuccess']) {
        const {pages, field} = action.payload;
        return {
            ...state,
            pages: pages,
            selectedField: field,
            activeTab: 2,
            forceUpdateActiveTab: true,
            enableBack: false,
            enableTextLocation: true,
            enableValidator: false,
            enableFormat: false,
            enableMouseMove: false,
        };
    } else if (action.type === actionTypes['addStepSuccess']) {
        const {pages, disableTextLocation, selectedField, locationMap} = action.payload;
        return {
            ...state,
            enableFormat: true,
            enableValidator: true,
            enableTextLocation: !disableTextLocation,
            enableBack: true,
            pages,
            selectedField,
            locationMap,
        };
    } else if (action.type === actionTypes['addAlternativeStep']) {
        return {...state};
    } else if (action.type === actionTypes['setControlMode']) {
        return {...state, controlMode: action.payload, focusAnnotationTool: false};
    } else if (action.type === actionTypes['finishUserGuide']) {
        localStorage.setItem('skipUserGuide', true);
        return {...state, showUserGuide: false};
    } else if (action.type === actionTypes['toggleUserGuide']) {
        return {...state, showUserGuide: !state.showUserGuide};
    } else if (action.type === actionTypes['setEnableMouseMove']) {
        return {...state, enableMouseMove: action.payload};
    } else if (action.type === actionTypes['setGeneralInformationSuccess']) {
        const {values, enableSaveAction} = action.payload;
        return {...state, ...values, enableSaveAction: enableSaveAction};
    } else if (action.type === actionTypes['loadTemplate']) {
        return {...state, isEditTemplate: true};
    } else if (action.type === actionTypes['loadTemplateSuccess']) {
        let {content} = action.payload;
        content = JSON.parse(content);
        return {
            ...state,
            uploadImage: content.pages[0].image,
            templateSession: content['resource_id'],
            templateName: content.title,
            description: content.description,
            removeAccent: content.removeAccent,
            fullText: content.fullText,
            detectQR: content.detectQRCode,
            detectSignature: content.detectSignature,
            detectMRZ: content.detectMRZ,
            skipImageProcessing: content.skipProcessing,
            scannedDocument: content.isScanned,
            pages: content.pages,
            isEditTemplate: false,
        };
    } else if (action.type === actionTypes['clearTemplate']) {
        return {...state, ...INIT_STATE, redirectToList: true};
    } else if (action.type === actionTypes['resetRedirectList']) {
        return {...state, redirectToList: false};
    } else if (action.type === actionTypes['switchField']) {
        const fieldId = action.payload;
        let selectedField = null;
        if (fieldId) {
            selectedField = state.pages[state.currentPage].fields.find(f => equals(f.id, fieldId));
        }
        return {...state, selectedField: selectedField};
    } else if (action.type === actionTypes['toggleAlternative']) {
        const {showAlternative, selectedStep, selectedNode} = action.payload;
        return {...state, showAlternative: showAlternative, selectedStep: selectedStep, selectedNode: selectedNode};
    } else if (action.type === actionTypes['addAlternativeStepSuccess']) {
        const {pages} = action.payload;
        const selectedField = pages[state.currentPage].fields.find(f => equals(f.id, state.selectedField.id));
        return {...state, pages: pages, selectedField: selectedField, selectedStep: null, selectedNode: null};
    } else if (action.type === actionTypes['changeFieldName']) {
        const {name} = action.payload;
        const pages = {...state.pages};
        pages[state.currentPage].fields = pages[state.currentPage].fields.map(f => {
            const field = {...f};
            if (field.id === state.selectedField.id) {
                field.name = name;
            }
            return field;
        });
        const selectedField = pages[state.currentPage].fields.find(f => equals(f.id, state.selectedField.id));
        return {...state, pages, selectedField};
    } else {
        return {...state};
    }
};

export {actions, actionTypes, templateReducers};
